/**
 * eventLog.ts — Single source of truth for product event instrumentation.
 * Writes to `event_log` table (DB-backed).
 * Optionally mirrors to Mixpanel for external analytics.
 */
import { supabase } from '@/integrations/supabase/client';
import { track as mixpanelTrack } from './mixpanel';

export interface EventProps {
  project_id?: string;
  [key: string]: unknown;
}

/**
 * Log a product event to the event_log table and mirror to Mixpanel.
 * Fails silently — never blocks the UI.
 */
export async function logEvent(
  eventName: string,
  props: EventProps = {}
) {
  try {
    // Use getSession() (reads cached session from storage) instead of getUser()
    // which hits /auth/v1/user. getUser() returns null whenever the refresh
    // token is stale (very common — see refresh_token_not_found warnings),
    // which silently dropped almost every event. getSession() is faster and
    // tolerates transient refresh issues.
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;

    const { project_id, ...rest } = props;

    const { error } = await (supabase.from('event_log' as any) as any).insert({
      event_name: eventName,
      user_id: userId,
      project_id: project_id || null,
      properties: rest,
    });

    if (error) {
      console.warn('[eventLog] Insert failed:', eventName, error.message);
      return;
    }

    // Mirror to Mixpanel (optional, non-blocking)
    mixpanelTrack(eventName, { ...props, source: 'event_log' });
  } catch (e) {
    console.warn('[eventLog] Failed:', eventName, e);
  }
}
